import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// Resize a given column by an amount from the current state
import { tableCellMinWidth } from '@atlaskit/editor-common/styles';
import { akEditorFullWidthLayoutWidth, akEditorGutterPaddingDynamic, akEditorTableNumberColumnWidth } from '@atlaskit/editor-shared-styles';
import { TableCssClassName as ClassName } from '../../../types';
import { ALIGN_CENTER, ALIGN_START, shouldChangeAlignmentToCenterResized } from '../../../utils/alignment';
import { getTableScalingPercent } from './misc';
import { growColumn, shrinkColumn, updateAffectedColumn } from './resize-logic';
import { updateColgroup } from './resize-state';
export var resizeColumn = function resizeColumn(resizeState, colIndex, amount, tableRef, tableNode, selectedColumns) {
  var isTableScalingEnabled = arguments.length > 6 && arguments[6] !== undefined ? arguments[6] : false;
  var shouldUseIncreasedScalingPercent = arguments.length > 7 && arguments[7] !== undefined ? arguments[7] : false;
  var scalePercent = 1;
  var resizeAmount = amount;
  if (isTableScalingEnabled) {
    scalePercent = getTableScalingPercent(tableNode, tableRef, shouldUseIncreasedScalingPercent);
    resizeAmount = amount / scalePercent;
  }
  var newState = resizeAmount > 0 ? growColumn(resizeState, colIndex, resizeAmount, selectedColumns) : resizeAmount < 0 ? shrinkColumn(resizeState, colIndex, resizeAmount, selectedColumns) : resizeState;
  updateColgroup(newState, tableRef, tableNode, isTableScalingEnabled, shouldUseIncreasedScalingPercent);
  return newState;
};
export var resizeColumnAndTable = function resizeColumnAndTable(_ref) {
  var resizeState = _ref.resizeState,
    colIndex = _ref.colIndex,
    amount = _ref.amount,
    tableRef = _ref.tableRef,
    tableNode = _ref.tableNode,
    lineLength = _ref.lineLength,
    editorWidth = _ref.width,
    isTableAlignmentEnabled = _ref.isTableAlignmentEnabled;
  var editorContainerWidth = getEditorContainerWidth(editorWidth);
  var isTableLeftAligned = tableNode.attrs.layout === ALIGN_START;
  var isNumberColumnEnabled = tableNode.attrs.isNumberColumnEnabled;
  var isOverflow = resizeState.tableWidth > resizeState.maxSize;
  var resizeAmount = isTableLeftAligned ? amount : amount * 2;
  var willTableHitEditorEdge = resizeState.maxSize + resizeAmount > editorContainerWidth;
  var willResizedTableStayInOverflow = isOverflow && resizeState.tableWidth + resizeAmount / 2 > resizeState.maxSize;

  // STEP 1: Update col width
  if (willTableHitEditorEdge || willResizedTableStayInOverflow) {
    var _tableRef$closest;
    var tableContainerWidth = (_tableRef$closest = tableRef.closest('.pm-table-container')) === null || _tableRef$closest === void 0 ? void 0 : _tableRef$closest.clientWidth;
    resizeAmount = amount < 0 ? amount : resizeAmount - (resizeState.maxSize + resizeAmount - tableContainerWidth) / 2;
  } else {
    var diff = -(resizeState.tableWidth - resizeState.maxSize);
    var rest = amount - diff;
    var final = isTableLeftAligned ? diff + rest : diff + rest * 2;
    resizeAmount = final;
  }
  var newState = updateAffectedColumn(resizeState, colIndex, resizeAmount);

  // STEP 2: Update table container width
  // columns have a min width, so delta !== resizeAmount when this is reached, use this for calculations
  var delta = newState.cols[colIndex].width - resizeState.cols[colIndex].width;
  newState.maxSize = Math.round(isOverflow ? willResizedTableStayInOverflow ?
  // CASE 1A: table will stay in overflow
  // do not grow the table because resize is happening in the overflow region
  // and the overall table container needs to be retained
  isNumberColumnEnabled ? resizeState.maxSize + akEditorTableNumberColumnWidth : resizeState.maxSize :
  // CASE 1B: table will no longer be in overflow, so adjust container width
  // ensure the table is resized without any 'big jumps' by working out
  // the difference between the new table width and the max size and adding the resize
  isNumberColumnEnabled ? resizeState.maxSize + akEditorTableNumberColumnWidth + (resizeState.tableWidth - resizeState.maxSize + akEditorTableNumberColumnWidth + delta) : resizeState.maxSize + (resizeState.tableWidth - resizeState.maxSize + delta) : willTableHitEditorEdge ?
  // CASE 2: table will hit editor edge
  editorContainerWidth :
  // CASE 3: table is being resized from a non-overflow state
  isNumberColumnEnabled ? resizeState.maxSize + akEditorTableNumberColumnWidth + delta : resizeState.maxSize + delta);

  // do not apply scaling logic because resize state is already scaled
  updateColgroup(newState, tableRef, tableNode, false, false);
  updateTablePreview(tableRef, newState.maxSize, shouldChangeAlignmentToCenterResized(isTableAlignmentEnabled, tableNode, lineLength, newState.maxSize));
  return newState;
};
var updateTablePreview = function updateTablePreview(tableRef, newTableWidth, shouldChangeAlignment) {
  var resizingContainer = tableRef.closest(".".concat(ClassName.TABLE_RESIZER_CONTAINER));
  var resizingItem = resizingContainer === null || resizingContainer === void 0 ? void 0 : resizingContainer.querySelector('.resizer-item');
  var alignmentContainer = resizingContainer === null || resizingContainer === void 0 ? void 0 : resizingContainer.parentElement;
  if (resizingItem) {
    var newWidth = "".concat(newTableWidth, "px");
    resizingContainer.style.width = newWidth;
    resizingItem.style.width = newWidth;
    if (shouldChangeAlignment && alignmentContainer) {
      alignmentContainer.style.justifyContent = ALIGN_CENTER;
    }
  }
};
var getEditorContainerWidth = function getEditorContainerWidth(editorWidth) {
  return Math.min(editorWidth - akEditorGutterPaddingDynamic() * 2, akEditorFullWidthLayoutWidth);
};

/**
 * Apply a scaling factor to resize state
 */
export var scaleResizeState = function scaleResizeState(_ref2) {
  var resizeState = _ref2.resizeState,
    tableRef = _ref2.tableRef,
    tableNode = _ref2.tableNode,
    editorWidth = _ref2.editorWidth;
  var isNumberColumnEnabled = tableNode.attrs.isNumberColumnEnabled;
  var isTableScaled = isNumberColumnEnabled || resizeState.maxSize > getEditorContainerWidth(editorWidth);

  // Tables with number column can cause the table to be in two different states:
  // 1. The table sum of col widths will be smaller than the max size, which is incorrect. For this
  // avoid scaling and take the document width
  // 2. The table sum of col widths will be the same size as max width, which happens when the table
  // is scaled using preserve table width logic, for this apply a scaled width
  // return early if table isn't scaled
  if (!isTableScaled || isNumberColumnEnabled && resizeState.maxSize > resizeState.tableWidth) {
    return resizeState;
  }
  var scalePercent = getTableScalingPercent(tableNode, tableRef);
  var scaledTableWidth = Math.round(resizeState.tableWidth * scalePercent);
  var cols = resizeState.cols.map(function (col) {
    return _objectSpread(_objectSpread({}, col), {}, {
      minWidth: tableCellMinWidth,
      width: Math.max(Math.round(col.width * scalePercent), tableCellMinWidth)
    });
  });
  var calculatedTableWidth = cols.reduce(function (prev, curr) {
    return prev + curr.width;
  }, 0);

  // using Math.round can cause the sum of col widths to be larger than the table width
  // distribute the difference to the first column
  if (calculatedTableWidth > scaledTableWidth) {
    var diff = calculatedTableWidth - scaledTableWidth;
    var hasDiffBeenDistributed = false;
    cols = cols.map(function (col) {
      if (!hasDiffBeenDistributed && col.width - diff >= col.minWidth) {
        hasDiffBeenDistributed = true;
        return _objectSpread(_objectSpread({}, col), {}, {
          width: col.width - diff
        });
      }
      return col;
    });
  }
  var maxSize = isNumberColumnEnabled ? Math.round((resizeState.maxSize + akEditorTableNumberColumnWidth) * scalePercent) : Math.round(resizeState.maxSize * scalePercent);
  return _objectSpread(_objectSpread({}, resizeState), {}, {
    widths: cols.map(function (col) {
      return col.width;
    }),
    tableWidth: scaledTableWidth,
    maxSize: maxSize,
    cols: cols
  });
};